import { FishCatchComponent } from './fish-catch-button.component';
import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { interval, Subscription } from 'rxjs';
import { getAccuratePosition } from '../utils';
import { WeatherService, WeatherData, TideData } from '../services/weather.service';

interface FishType {
  fish_name: string;
  fish_display_color: string;
  fish_full_name: string;
}

@Component({
  standalone: true, 
  selector: 'app-fishing',
  imports: [ MatButtonModule, CommonModule, MatDividerModule, FishCatchComponent ],
  templateUrl: './fishing.component.html',
  styleUrl: './fishing.component.scss'
})
export class FishingComponent implements OnInit, OnDestroy {
  isActive: boolean = false;
  startTime: string | null = null;
  tripId: number | null = null;
  public allFishTypes: FishType[] = [];
  private locationSubscription?: Subscription;
  lastLocationUpdate?: Date;
  locationUpdateCount = 0;
  locationTrackingEnabled = true;

  currentWeather: WeatherData | null = null;
  currentTide: TideData | null = null;

  wakeLockEnabled = false;
  private wakeLock: WakeLockSentinel | null = null;

  constructor(private http: HttpClient, private weatherService: WeatherService) {}

  ngOnInit(): void {
    this.checkTripStatus().then(() => this.loadFishTypes());
  }

  ngOnDestroy() {
    this.stopLocationTracking();
    this.releaseWakeLock();
  }

  private startLocationTracking() {
    if (!this.locationTrackingEnabled) return;
    this.stopLocationTracking();
    this.getLocation();
    this.locationSubscription = interval(300000).subscribe(() => this.getLocation());
  }

  private stopLocationTracking() {
    if (this.locationSubscription) {
      this.locationSubscription.unsubscribe();
      this.locationSubscription = undefined;
    }
  }

  private async getLocation(): Promise<void> {
    let position: GeolocationPosition;
    try {
      position = await getAccuratePosition();

      // If the first fix is coarse (GPS not yet warmed up), retry with a longer
      // window so the satellite signal has more time to lock in.
      if (position.coords.accuracy > 50) {
        try {
          position = await getAccuratePosition(50, 20000);
        } catch {
          // keep the first result — always post something
        }
      }
    } catch (err) {
      console.error('Geolocation error:', err);
      return;
    }

    const locationData = {
      type: 'location',
      lat: position.coords.latitude,
      long: position.coords.longitude,
      accuracy: position.coords.accuracy,
    };
    this.http.post(`${environment.apiUrl}/caught_fish`, locationData).subscribe({
      next: () => {
        this.locationUpdateCount++;
        this.lastLocationUpdate = new Date();
        this.fetchWeatherAndTide(position.coords.latitude, position.coords.longitude);
      },
      error: (err) => console.error('Error saving location:', err),
    });
  }

  toggleLocationTracking(): void {
    this.locationTrackingEnabled = !this.locationTrackingEnabled;
    if (this.locationTrackingEnabled && this.isActive) {
      this.startLocationTracking();
    } else {
      this.stopLocationTracking();
      this.currentWeather = null;
      this.currentTide = null;
    }
  }

  fetchWeatherAndTide(lat: number, lng: number) {
    this.weatherService.getWeather(lat, lng).subscribe({
      next: (data) => this.currentWeather = data,
      error: (err) => console.error('Error fetching weather:', err)
    });
    this.weatherService.getTides(lat, lng).subscribe({
      next: (data) => this.currentTide = data,
      error: (err) => console.error('Error fetching tide:', err)
    });
  }

  get formattedTideTime(): string {
    if (!this.currentTide || !this.currentTide.minutes_until_next_tide) return '';
    const minsStr = this.currentTide.minutes_until_next_tide;
    const mins = parseInt(minsStr, 10);
    if (isNaN(mins)) return minsStr;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return `${h}h${m}m`;
  }

  async toggleWakeLock(): Promise<void> {
    this.wakeLockEnabled = !this.wakeLockEnabled;
    if (this.wakeLockEnabled) {
      await this.requestWakeLock();
    } else {
      await this.releaseWakeLock();
    }
  }

  private async requestWakeLock(): Promise<void> {
    try {
      this.wakeLock = await navigator.wakeLock.request('screen');
      this.wakeLock.addEventListener('release', () => {
        if (this.wakeLockEnabled) {
          document.addEventListener('visibilitychange', this.onVisibilityChange);
        }
      });
    } catch (err) {
      console.error('Wake lock failed:', err);
      this.wakeLockEnabled = false;
    }
  }

  private async releaseWakeLock(): Promise<void> {
    await this.wakeLock?.release();
    this.wakeLock = null;
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
  }

  private onVisibilityChange = async () => {
    if (document.visibilityState === 'visible' && this.wakeLockEnabled) {
      await this.requestWakeLock();
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
    }
  };

  checkTripStatus(): Promise<void> {
    return new Promise((resolve) => {
      this.http.get<{ active: boolean; start_time: string | null; trip_id: number | null }>(
        `${environment.apiUrl}/fishing_trip`
      ).subscribe({
        next: (data) => {
          const wasActive = this.isActive;
          this.isActive = data.active;
          this.startTime = data.start_time;
          this.tripId = data.trip_id;
          if (this.isActive && !wasActive) {
            this.startLocationTracking();
          } else if (!this.isActive && wasActive) {
            this.stopLocationTracking();
          }
          resolve();
        },
        error: (err) => {
          console.error('Error fetching trip status:', err);
          resolve();
        }
      });
    });
  }

  private loadFishTypes(): void {
    this.http.get<FishType[]>(`${environment.apiUrl}/fish_types`).subscribe({
      next: (data: any) => {
        this.allFishTypes = Object.values(data);
      },
      error: (error) => {
        console.error('Error loading fish types:', error);
      }
    });
  }

  toggleTrip() {
    const action = this.isActive ? 'stop' : 'start';
    this.http.post(`${environment.apiUrl}/fishing_trip`, { action })
      .subscribe({
        next: (response) => {
          console.log('Trip action success:', response);
          this.checkTripStatus();
        },
        error: (err) => {
          console.error('Trip action error:', err);
        }
      });
  }
}